// components/Navbar.tsx
"use client";
import { useState, useEffect } from "react";
import {
  Flex,
  Button,
  Image,
  useDisclosure,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  useToast,
  Show,
  Portal,
} from "@chakra-ui/react";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import DeepMode from "@public/DeepMode.svg";
import GoogleForm from "./GoogleForm";
import { useAppContext } from "hooks";
import { LuCoins } from "react-icons/lu";
import { useConsentUpdate } from "queries";

const Navbar = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { jwt, profile, logOut } = useAppContext();
  const [nsfwConsent, setNsfwConsent] = useState<boolean | undefined>(profile?.nsfw_consent);
  const updateConsentsMutation = useConsentUpdate();
  const toast = useToast();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    setNsfwConsent(profile?.nsfw_consent);
  }, [profile]);

  const handlePreferenceChange = () => {
    updateConsentsMutation.mutate(
      {
        nsfwConsent: !nsfwConsent,
        adultConsent: profile!.adult_consent,
        newsletterConsent: profile!.newsletter_consent,
      },
      {
        onSuccess: (response) => {
          setNsfwConsent(!nsfwConsent);
          toast({
            title: "Success",
            description: (response as any).message,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        },
      }
    );
  };

  return (
    <>
      {/* Mobile, < 768px */}
      <Show below="md-1">
        {!profile ? (
          <Flex zIndex={20} px="32px" align="center" mt="20px" justify="space-between">
            <Link href="/">
              <Image alt="DeepMode logo" w="128px" src={DeepMode.src} />
            </Link>
            <Flex align="center">
              <Button w="90px" p="1px" bg="button.500" onClick={onOpen}>
                <Text fontWeight="bold" fontSize="14px" color="white">
                  Sign Up
                </Text>
              </Button>
            </Flex>
            <GoogleForm isOpen={isOpen} onClose={onClose} />
          </Flex>
        ) : (
          <Flex zIndex={5} direction="column">
            <Flex
              align="center"
              px="32px"
              zIndex={5}
              justifyContent="space-between"
              w="100%"
              mt="25px"
            >
              <Link href="/">
                <Image maxW="131px" src={DeepMode.src} />
              </Link>
              <Flex align="center">
                <Menu>
                  <MenuButton>
                    <Flex
                      h="25px"
                      align="center"
                      borderRadius="8px"
                      justify="center"
                      p={2}
                      bg="button.500"
                    >
                      <Text fontSize="14px" fontWeight="semibold">
                        {profile?.credit} credits
                      </Text>
                    </Flex>
                  </MenuButton>
                  <Portal>
                  <MenuList borderRadius="12px" p="8px" bg="gray.700" border="1px solid #FF1B6D">
                    <Flex direction="column" align="center">
                      <Text fontSize="12px">Balance</Text>
                      <Flex align="center">
                        <LuCoins />
                        <Text
                          fontWeight="semibold"
                          ml="2px"
                          fontSize="20px"
                          bgGradient="linear(to-r, #FFB5FF, #1AA5D3)"
                          bgClip="text"
                        >
                          {profile?.credit} credits
                        </Text>
                      </Flex>
                      {profile?.credit! < 5 ? (
                        <Text mt="10px" w="300px" textAlign="center" fontSize="12px">
                          Your balance is low! Top-up your credits to continue generating.
                        </Text>
                      ) : (
                        ""
                      )}
                      <Flex w="100%" h="1px" bg="#2B2B2C" mt="15px" />
                      <Button
                        onClick={() => router.push("/payments")}
                        mb="20px"
                        w="90%"
                        mt="20px"
                        bg="#FF1B6D"
                      >
                        <Text fontSize="14px" color="white" fontWeight="semibold">
                          Top-up!
                        </Text>
                      </Button>
                    </Flex>
                  </MenuList>
                  </Portal>
                </Menu>
                <Menu>
                  <MenuButton bg="transparent" variant="unstyled" as={Button}>
                    <Flex align="center">
                      <Avatar
                        ml="5px"
                        name={profile?.first_name || "User"}
                        src={profile?.picture}
                        h="35px"
                        w="35px"
                      />
                    </Flex>
                  </MenuButton>
                  <Portal>
                  <MenuList bg="gray.700" border="1px solid #FF1B6D">
                    {/* Conditionally render the Dashboard menu item */}
                    {pathname !== "/dashboard" && (
                      <MenuItem bg="gray.700" onClick={() => router.push("/dashboard")}>
                        Dashboard
                      </MenuItem>
                    )}
                    <MenuItem bg="gray.700" onClick={() => router.push("/profile")}>
                      Profile
                    </MenuItem>
                    <MenuItem bg="gray.700" onClick={logOut}>
                      Log out
                    </MenuItem>
                  </MenuList>
                  </Portal>
                </Menu>
              </Flex>
            </Flex>
          </Flex>
        )}
      </Show>
      {/* Desktop, >= 768px */}
      <Show above="md">
        <Flex
          px={["25px", "35px", "40px", "60px", "60px", "15%"]}
          zIndex={5}
          mt="35px"
          w="100%"
          justify="space-between"
          align="center"
        >
          <Link href="/">
            <Image src={DeepMode.src} alt="DeepMode Logo" />
          </Link>
          <Flex w="100%" justify={{ base: "end", md: "end", lg: "end" }} align="center">
            {jwt ? (
              <Flex align="center">
                <Menu>
                  <MenuButton
                    h="38px"
                    fontSize="14px"
                    fontWeight="bold"
                    px="24px"
                    mr="10px"
                    borderRadius="16px"
                    boxShadow="0 0 4px 4px rgba(255, 27, 109, 0.4)"
                    bg="#FF1B6D"
                  >
                    <Flex align="center">
                      <LuCoins />

                      <Text ml="2px">
                        {profile?.credit !== undefined && profile?.credit !== null
                          ? `Balance: ${profile?.credit} credits`
                          : ""}
                      </Text>
                    </Flex>
                  </MenuButton>
                  <Portal>
                  <MenuList borderRadius="12px" p="8px" bg="gray.700" border="1px solid #FF1B6D">
                    <Flex direction="column" align="center">
                      <Text fontSize="12px">Balance</Text>
                      <Flex align="center">
                        <LuCoins />
                        <Text
                          fontWeight="semibold"
                          ml="2px"
                          fontSize="20px"
                          bgGradient="linear(to-r, #FFB5FF, #1AA5D3)"
                          bgClip="text"
                        >
                          {profile?.credit} credits
                        </Text>
                      </Flex>
                      {profile?.credit! < 5 ? (
                        <Text mt="10px" w="300px" textAlign="center" fontSize="12px">
                          Your balance is low! Top-up your credits to continue generating.
                        </Text>
                      ) : (
                        ""
                      )}
                      <Flex w="100%" h="1px" bg="#2B2B2C" mt="15px" />
                      <Button
                        onClick={() => router.push("/payments")}
                        mb="20px"
                        w="90%"
                        mt="20px"
                        bg="#FF1B6D"
                      >
                        <Text fontSize="14px" color="white" fontWeight="semibold">
                          Top-up!
                        </Text>
                      </Button>
                    </Flex>
                  </MenuList>
                  </Portal>
                </Menu>
                <Menu>
                  <MenuButton borderRadius={50} as={Button} p={0}>
                    <Avatar name={profile?.first_name || "User"} src={profile?.picture} />
                  </MenuButton>
                  <Portal>
                  <MenuList bg="gray.700" border="1px solid #FF1B6D">
                    {pathname !== "/dashboard" && (
                      <MenuItem bg="gray.700" onClick={() => router.push("/dashboard")}>
                        Dashboard
                      </MenuItem>
                    )}
                    <MenuItem bg="gray.700" onClick={() => router.push("/profile")}>
                      Profile
                    </MenuItem>
                    <MenuItem bg="gray.700" onClick={logOut}>
                      Log out
                    </MenuItem>
                  </MenuList>
                  </Portal>
                </Menu>
              </Flex>
            ) : (
              <Flex>
                <Link href="/public-models">
                  <Button
                    px="24px"
                    h="56px"
                    borderRadius="16px"
                    border="1px solid white"
                    color="white"
                    bg="transparent"
                  >
                    <Text>Models</Text>
                  </Button>
                </Link>
                <Button
                  h="56px"
                  ml="24px"
                  px="24px"
                  color="white"
                  bg="#FF1B6D"
                  borderRadius="16px"
                  onClick={onOpen}
                >
                  Sign Up
                </Button>
              </Flex>
            )}
          </Flex>
          <GoogleForm isOpen={isOpen} onClose={onClose} />
        </Flex>
      </Show>
    </>
  );
};

export default Navbar;
